'use client';

import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, Search, AlertCircle } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  FormField,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@insolech/ui';
import {
  orderMutations,
  orderQueries,
  type CombinableDirectBilledOrder,
  type PaymentGroupOrderMember,
} from '@insolech/sdk';
import { supabase } from '@/lib/supabase';

/**
 * CombineOrdersDialog - admin-only modal that lets the operator stitch
 * multiple direct-billed orders into one Stripe Checkout payment group, or
 * regenerate the link for an existing group.
 *
 * Two modes:
 *   - mode="create": fresh combine, primary CTA mints a new group.
 *   - mode="edit": regenerate, pre-fills the group's existing membership
 *     and total. NOTE: the current SDK helper only mints fresh groups; in
 *     edit mode we call `regenerateGroupPaymentLink` on the same membership
 *     and total (membership editing is deferred to a follow-up that adds
 *     replace_payment_group_orders RPC support; the UI still lets the
 *     admin tweak the total).
 *
 * The current order is always present in the picker as a disabled, checked
 * row with a "This order" badge so the admin understands it cannot be
 * unchecked.
 */

export interface CombineOrdersDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** The order whose detail page hosts the dialog. Always pre-selected. */
  currentOrderId: string;
  /** Order number for the current order, shown in the picker row. */
  currentOrderNumber: string;
  /** Patient name for the current order's picker row (may be null). */
  currentOrderPatientName: string | null;
  /** total_aed for the current order; included in the running sum. */
  currentOrderTotalAed: number;
  /**
   * 'create' (default) for a fresh combine. 'edit' for the regenerate flow
   * from the CombinedGroupBanner, in which case `existingGroup` is required.
   */
  mode?: 'create' | 'edit';
  /**
   * Existing group metadata in edit mode. The dialog uses this to pre-select
   * membership and pre-fill the editable total.
   */
  existingGroup?: {
    id: string;
    total_aed: number;
    orders: PaymentGroupOrderMember[];
  } | null;
  /**
   * Called after a successful combine / regenerate. The parent is expected
   * to invalidate detail queries for every affected order id.
   */
  onSuccess: (args: { paymentGroupId: string; checkoutUrl: string; affectedOrderIds: string[] }) => void;
}

export function CombineOrdersDialog({
  open,
  onOpenChange,
  currentOrderId,
  currentOrderNumber,
  currentOrderPatientName,
  currentOrderTotalAed,
  mode = 'create',
  existingGroup = null,
  onSuccess,
}: CombineOrdersDialogProps) {
  const qc = useQueryClient();
  const isEdit = mode === 'edit' && existingGroup !== null;

  // Pull the list of combinable orders. In edit mode we exclude the current
  // order (it is always present as a disabled checked row) but the picker
  // also needs to surface the OTHER group siblings as already-checked rows;
  // we merge them in below.
  const combinableQuery = useQuery({
    queryKey: ['orders', 'combinable-direct-billed', { excludeOrderId: currentOrderId }],
    queryFn: () =>
      orderMutations.listCombinableDirectBilledOrders(supabase, {
        excludeOrderId: currentOrderId,
      }),
    enabled: open,
    staleTime: 30_000,
  });

  // Stitch the current order row + sibling rows (in edit mode) into the
  // candidate list. Siblings are not returned by the combinable query
  // (their payment_group_id is NOT NULL), so we synthesize lightweight
  // rows from the existingGroup payload.
  const candidateRows = React.useMemo<CombinableDirectBilledOrder[]>(() => {
    const fetched = combinableQuery.data ?? [];
    const rows: CombinableDirectBilledOrder[] = [];

    // 1) Current order, always present at top.
    rows.push({
      id: currentOrderId,
      order_number: currentOrderNumber,
      patient_name: currentOrderPatientName,
      created_at: '',
      product_label: '',
      total_aed: currentOrderTotalAed,
      payment_group_id: existingGroup?.id ?? null,
    });

    // 2) Existing siblings (edit mode), inserted after the current order.
    if (isEdit && existingGroup) {
      for (const sib of existingGroup.orders) {
        if (sib.id === currentOrderId) continue;
        rows.push({
          id: sib.id,
          order_number: sib.order_number,
          patient_name: sib.patient?.full_name ?? null,
          created_at: '',
          product_label: '',
          total_aed: sib.total_aed,
          payment_group_id: existingGroup.id,
        });
      }
    }

    // 3) Everything else returned by the picker query.
    const seen = new Set(rows.map((r) => r.id));
    for (const r of fetched) {
      if (seen.has(r.id)) continue;
      rows.push(r);
    }
    return rows;
  }, [
    combinableQuery.data,
    currentOrderId,
    currentOrderNumber,
    currentOrderPatientName,
    currentOrderTotalAed,
    isEdit,
    existingGroup,
  ]);

  // Selection state. Pre-selected: the current order (always), plus every
  // existing sibling when in edit mode.
  const initialSelection = React.useMemo(() => {
    const set = new Set<string>([currentOrderId]);
    if (isEdit && existingGroup) {
      for (const sib of existingGroup.orders) set.add(sib.id);
    }
    return set;
  }, [currentOrderId, isEdit, existingGroup]);

  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(initialSelection);
  const [filterText, setFilterText] = React.useState('');
  const [totalAedText, setTotalAedText] = React.useState<string>('');
  const [didInitTotal, setDidInitTotal] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [successUrl, setSuccessUrl] = React.useState<string | null>(null);

  // Reset state every time the dialog re-opens.
  React.useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set(initialSelection));
    setFilterText('');
    setErrorMessage(null);
    setSuccessUrl(null);
    setDidInitTotal(false);
  }, [open, initialSelection]);

  // Auto-fill the total once we have data the first time the dialog opens.
  // After that, the user is free to edit it (we do not reset on selection
  // change; admins typically want to keep their discount even if they add
  // or remove an order, so we leave the field alone).
  const sumOfSelected = React.useMemo(() => {
    let s = 0;
    for (const r of candidateRows) {
      if (selectedIds.has(r.id)) s += r.total_aed;
    }
    return Math.round(s * 100) / 100;
  }, [candidateRows, selectedIds]);

  React.useEffect(() => {
    if (!open || didInitTotal) return;
    if (isEdit && existingGroup) {
      setTotalAedText(existingGroup.total_aed.toFixed(2));
      setDidInitTotal(true);
      return;
    }
    // create mode: wait until candidate rows are present so the sum is
    // meaningful (covers loading -> data transition).
    if (combinableQuery.isSuccess || candidateRows.length > 0) {
      setTotalAedText(sumOfSelected.toFixed(2));
      setDidInitTotal(true);
    }
  }, [
    open,
    didInitTotal,
    isEdit,
    existingGroup,
    combinableQuery.isSuccess,
    candidateRows.length,
    sumOfSelected,
  ]);

  const filteredRows = React.useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return candidateRows;
    return candidateRows.filter((r) => {
      const num = r.order_number.toLowerCase();
      const patient = (r.patient_name ?? '').toLowerCase();
      return num.includes(q) || patient.includes(q);
    });
  }, [candidateRows, filterText]);

  const toggle = (id: string) => {
    if (id === currentOrderId) return; // immutable
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalAed = React.useMemo(() => {
    const n = Number.parseFloat(totalAedText);
    return Number.isFinite(n) ? n : NaN;
  }, [totalAedText]);

  const combineMut = useMutation({
    mutationFn: async () => {
      const ids = Array.from(selectedIds);
      if (isEdit && existingGroup) {
        // Edit mode: keep current membership (membership edits require an
        // RPC that does not exist yet); refresh the total + mint new Stripe
        // session. We pass the current member ids alongside the group id
        // because the Edge Function's input gate requires either order_id
        // or order_ids on every request; payment_group_id alone returns
        // 400 from the validation_error branch.
        const memberIds = existingGroup.orders.map((o) => o.id);
        const res = await orderMutations.regenerateGroupPaymentLink(
          supabase,
          existingGroup.id,
          memberIds,
          totalAed,
        );
        return {
          paymentGroupId: existingGroup.id,
          checkoutUrl: res.checkoutUrl,
          affectedOrderIds: memberIds,
        };
      }
      const res = await orderMutations.combineDirectBillingOrders(supabase, {
        orderIds: ids,
        totalAed,
      });
      return {
        paymentGroupId: res.groupId,
        checkoutUrl: res.checkoutUrl,
        affectedOrderIds: ids,
      };
    },
    onSuccess: async (result) => {
      setErrorMessage(null);
      setSuccessUrl(result.checkoutUrl);
      // Best-effort copy to clipboard. We do not surface a copy error here
      // because the URL is rendered in the success Alert below.
      try {
        await navigator.clipboard.writeText(result.checkoutUrl);
      } catch {
        /* clipboard unavailable; URL is visible in the Alert */
      }
      // Invalidate every affected order detail query so the banner updates.
      for (const oid of result.affectedOrderIds) {
        void qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.detail(oid) });
      }
      void qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.lists() });
      void qc.invalidateQueries({
        queryKey: ['orders', 'combinable-direct-billed'],
      });
      // Invalidate the payment-group query so DirectBillingPaymentCard's
      // paymentGroup.stripe_checkout_url re-fetches the freshly-stamped URL.
      // Without this, the card's Copy button copies the prior (now-expired)
      // Stripe session URL until a hard reload.
      void qc.invalidateQueries({
        queryKey: ['orders', 'payment-group', result.paymentGroupId],
      });
      onSuccess(result);
    },
    onError: (e) => {
      setSuccessUrl(null);
      setErrorMessage(e instanceof Error ? e.message : String(e));
    },
  });

  const primaryDisabled =
    combineMut.isPending ||
    selectedIds.size < 2 ||
    !Number.isFinite(totalAed) ||
    totalAed <= 0;

  const primaryLabel = isEdit ? 'Regenerate combined payment link' : 'Create combined payment link';
  const primaryPendingLabel = isEdit ? 'Regenerating link...' : 'Creating link...';

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="xl">
        <ModalHeader>
          <ModalTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-brand-600" aria-hidden="true" />
            {isEdit ? 'Regenerate combined payment link' : 'Combine orders into one payment'}
          </ModalTitle>
          <ModalDescription>
            {isEdit
              ? 'Adjust the total and regenerate a fresh Stripe Checkout link. The old link will no longer be valid.'
              : 'Pick the direct-billed orders to combine. A single Stripe Checkout link will be generated for the total.'}
          </ModalDescription>
        </ModalHeader>

        <ModalBody className="space-y-4">
          {/* Filter */}
          <div>
            <Input
              type="text"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="Filter by patient name or order number"
              startAdornment={<Search className="h-4 w-4 text-text-muted" aria-hidden="true" />}
            />
          </div>

          {/* Table */}
          <div className="rounded-md border border-border-subtle">
            <Table wrapperClassName="max-h-[45vh] overflow-auto">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <span className="sr-only">Select</span>
                  </TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead className="text-end">Amount (AED)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {combinableQuery.isLoading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      <TableCell>
                        <Skeleton className="h-4 w-4" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-40" />
                      </TableCell>
                      <TableCell className="text-end">
                        <Skeleton className="ms-auto h-4 w-16" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : combinableQuery.isError ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Alert tone="error" title="Could not load orders">
                        {combinableQuery.error instanceof Error
                          ? combinableQuery.error.message
                          : 'Unexpected error loading combinable orders.'}
                      </Alert>
                    </TableCell>
                  </TableRow>
                ) : filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <EmptyState
                        size="sm"
                        icon={<Layers className="h-5 w-5" />}
                        title="No matching orders"
                        description={
                          filterText
                            ? 'No orders match your filter. Try clearing it.'
                            : 'There are no other direct-billed unpaid orders to combine right now.'
                        }
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((row) => {
                    const isCurrent = row.id === currentOrderId;
                    const isChecked = selectedIds.has(row.id);
                    return (
                      <TableRow
                        key={row.id}
                        data-state={isChecked ? 'selected' : undefined}
                      >
                        <TableCell>
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-border-default text-brand-600"
                            checked={isChecked}
                            disabled={isCurrent}
                            onChange={() => toggle(row.id)}
                            aria-label={`Select order ${row.order_number}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{row.order_number}</span>
                            {isCurrent ? (
                              <Badge variant="brand" size="sm">
                                This order
                              </Badge>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell>{row.patient_name ?? '-'}</TableCell>
                        <TableCell className="text-end tabular-nums">
                          {row.total_aed.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Summary strip */}
          <div className="flex flex-col gap-3 rounded-md border border-border-subtle bg-sand-50 p-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="text-sm">
              <p className="text-text-secondary">
                <span className="font-medium text-text-primary">
                  {selectedIds.size} {selectedIds.size === 1 ? 'order' : 'orders'} selected
                </span>
              </p>
              <p className="text-xs text-text-muted">
                Sum AED <span className="tabular-nums">{sumOfSelected.toFixed(2)}</span>
              </p>
            </div>
            <div className="sm:w-56">
              <FormField
                label="Total to charge (AED)"
                helper="Override the sum to apply a discount."
              >
                <Input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={totalAedText}
                  onChange={(e) => setTotalAedText(e.target.value)}
                />
              </FormField>
            </div>
          </div>

          {/* Inline error */}
          {errorMessage ? (
            <Alert tone="error" title="Could not generate link" icon={<AlertCircle className="h-5 w-5" />}>
              {errorMessage}
            </Alert>
          ) : null}

          {/* Inline success */}
          {successUrl ? (
            <Alert tone="success" title={isEdit ? 'New payment link copied' : 'Payment link copied'}>
              <p className="break-all">
                The {isEdit ? 'previous link is no longer valid. New' : 'hosted'} link is:
              </p>
              <p className="mt-1 break-all font-mono text-xs">{successUrl}</p>
            </Alert>
          ) : null}
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={combineMut.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => combineMut.mutate()}
            disabled={primaryDisabled}
            isLoading={combineMut.isPending}
          >
            {combineMut.isPending ? primaryPendingLabel : primaryLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
